/**
 * The active-filter chip row for the search block. Shadows @plone/volto's
 * FilterList globally, so every block that renders facets gets it — the
 * search app AND every browse page. That is intentional: one filter-chip
 * treatment everywhere.
 *
 * Core renders this as a collapsed Semantic <Accordion> behind a
 * "Current filters applied: N" toggle with a trash-icon Clear button — which
 * hides the very state a facet-driven page most needs surfaced, and looks
 * nothing like the rest of this design. This rewrite is a small
 * always-visible row instead: `Filters: [Category: Guide ×] … Clear all`,
 * meant to sit directly above SearchControls's "Applied by default" row so
 * the two chip lines read as one system (see LeftColumnFacets.jsx for the
 * render order, SearchControls.jsx for the sibling row). The two rows report
 * DIFFERENT state on purpose: this one is facets the user picked (facet/URL
 * state); the defaults row is what the backend silently applied
 * (search_meta.*_defaulted). Neither replaces the other.
 *
 * Per-VALUE removal is still entirely core's: each facet widget type
 * registers a `filterListComponent` (SelectFacetFilterListEntry for
 * select/checkbox — and for the addon's archiveFacet, which reuses it, see
 * index.ts — DateRange/ToggleFacetFilterListEntry for whole-key removal) and
 * this file still resolves and renders those unchanged via `resolveExtension`.
 * Nothing here re-implements removal; only the container changed. Their DOM
 * (a Semantic `.ui.label` with a `.delete.icon`) is restyled to the shared
 * pill look in theme/search.less via the theme/chips.less mixins.
 */
import React from 'react';
import isEmpty from 'lodash/isEmpty';

import { resolveExtension } from '@plone/volto/helpers/Extensions';
import config from '@plone/volto/registry';

const FilterList = (props) => {
  const { data = {}, facets = {}, setFacets, isEditMode } = props;
  const definedFacets = data.facets || [];

  const totalFilters = definedFacets.filter(
    ({ field }) =>
      field &&
      Object.keys(facets).includes(field.value) &&
      !isEmpty(facets[field.value]),
  ).length;

  if (!totalFilters) return null;

  const { types: facetWidgetTypes } =
    config.blocks.blocksConfig.search.extensions.facetWidgets;

  return (
    <div className="pp-filterlist">
      <span className="pp-filterlist__label">Filters:</span>
      {definedFacets.map((facetSettings, i) => {
        const { filterListComponent: FilterListComponent } = resolveExtension(
          'type',
          facetWidgetTypes,
          facetSettings,
        );
        const facet = facetSettings?.field?.value;
        if (
          !facet ||
          !Object.keys(facets).includes(facet) ||
          isEmpty(facets[facet])
        ) {
          return null;
        }
        return (
          <div className="pp-filterlist__group" key={i}>
            <span className="pp-filterlist__group-title">
              {facetSettings.title ?? facetSettings?.field?.label}:
            </span>
            <FilterListComponent {...props} facet={facet} />
          </div>
        );
      })}
      <button
        type="button"
        className="pp-filterlist__clear"
        onClick={() => !isEditMode && setFacets({})}
      >
        Clear all
      </button>
    </div>
  );
};

export default FilterList;
